import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDateTime } from "@/lib/dates";
import { Printer, FileDown, FileSpreadsheet } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/compliance").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="text-slate-500">Loading…</div>;

  const downloadCsv = async (path, filename) => {
    const token = localStorage.getItem("cc_access_token");
    const resp = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 print-area">
      <header className="flex flex-wrap items-end justify-between gap-3 no-print">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">CQC inspection readiness</h1>
          <p className="text-sm text-slate-600">Evidence summary mapped to the Five Key Questions. Print or save as PDF.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadCsv("/reports/exports/incidents.csv", "incidents.csv")} data-testid="export-incidents" className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50"><FileSpreadsheet className="h-4 w-4" /> Incidents CSV</button>
          <button onClick={() => downloadCsv("/reports/exports/visits.csv", "visits.csv")} data-testid="export-visits" className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50"><FileSpreadsheet className="h-4 w-4" /> Visits CSV</button>
          <button onClick={() => downloadCsv("/reports/exports/safeguarding.csv", "safeguarding.csv")} data-testid="export-safeguarding" className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50"><FileSpreadsheet className="h-4 w-4" /> Safeguarding CSV</button>
          <button onClick={() => downloadCsv("/reports/exports/audit-log.csv", "audit-log.csv")} data-testid="export-audit" className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50"><FileSpreadsheet className="h-4 w-4" /> Audit log CSV</button>
          <button onClick={() => window.print()} data-testid="reports-print-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium"><Printer className="h-4 w-4" /> Print / save PDF</button>
        </div>
      </header>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 card-print">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Rabah Concept — Inspection Evidence Pack</h2>
        <p className="text-sm text-slate-600">Generated {fmtDateTime(new Date())}</p>
        <hr className="my-4 border-slate-200" />
        {Object.entries(data.five_key_questions || {}).map(([k, v]) => (
          <section key={k} className="mb-5">
            <h3 className="font-heading text-lg font-semibold text-[#0D7377] capitalize">{k.replace(/_/g, "-").toUpperCase()}</h3>
            <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {Object.entries(v).map(([metric, value]) => (
                <li key={metric} className="flex items-center justify-between border-b border-slate-100 py-2">
                  <span className="text-slate-700 capitalize">{metric.replace(/_/g, " ")}</span>
                  <strong className="text-slate-900 font-heading">{String(value)}</strong>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <hr className="my-4 border-slate-200" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Summary indicators</h3>
        <ul className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {Object.entries(data.summary || {}).map(([k, v]) => (
            <li key={k} className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={`summary-indicator-${k}`}>
              <p className="text-xs uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</p>
              {v && typeof v === "object" && !Array.isArray(v) ? (
                <div className="mt-1 space-y-0.5">
                  {Object.entries(v).map(([subK, subV]) => (
                    <p key={subK} className="text-sm text-slate-800 flex justify-between gap-2">
                      <span className="capitalize text-slate-600">{subK.replace(/_/g, " ")}</span>
                      <strong className="font-heading text-slate-900">{String(subV)}</strong>
                    </p>
                  ))}
                </div>
              ) : (
                <p className="font-heading font-bold text-lg text-slate-900">{Array.isArray(v) ? v.length : String(v)}</p>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs text-slate-500">This document supports compliance with the Health &amp; Social Care Act 2008 (Regulated Activities) Regulations 2014 and CQC Fundamental Standards (Regulations 9–20A).</p>
      </div>
    </div>
  );
}
